import type {OperatorNodeData, OperatorNode} from "@/view/workflow/components/workflowGraph/types";
import {GPTPromptRoleType, type GPTPrompt} from "@/model/llmSpecification";
import {type LLMModel, type Operator, OperatorTypeEnum} from "@/model/Workflow";
import {getOperatorNodeType} from "@/view/workflow/helper/baseOperatorHelper";

const DEFAULT_LLM_MODEL: LLMModel = {
    vendor: "",
    model: "",
    contextWindowSize: 128000,
    temperature: 0,
    maxTokens: 1000,
};

const DEFAULT_LLM_PROMPTS: GPTPrompt[] = [
    {role: GPTPromptRoleType.SYSTEM, content: ""},
    {role: GPTPromptRoleType.USER, content: ""},
];

const DEFAULT_PYTHON_CODE = "import json\ndef main(inlet):\n    \n    return inlet\n"

export const generateDefaultOperator = (
    operatorId: string,
    operatorType: OperatorTypeEnum,
    name: string
): Operator => {
    switch (operatorType) {
        case OperatorTypeEnum.BRANCH_PYTHON:
            return {
                _id: operatorId,
                type: OperatorTypeEnum.BRANCH_PYTHON,
                name: name,
                branchMap: {},
                code: DEFAULT_PYTHON_CODE,
            };
        case OperatorTypeEnum.FAN_IN:
            return {
                _id: operatorId,
                type: OperatorTypeEnum.FAN_IN,
                name: name,
            };
        case OperatorTypeEnum.FAN_OUT:
            return {
                _id: operatorId,
                type: OperatorTypeEnum.FAN_OUT,
                name: name,
                nextOperatorIds: [],
            };
        case OperatorTypeEnum.SEARCH_DATA_SOURCE_RAG:
            return {
                _id: operatorId,
                type: OperatorTypeEnum.SEARCH_DATA_SOURCE_RAG,
                name: name,
                mrcAnswerThreshold: 80,
                mrcThreshold: 50,
                faqThreshold: 80,
                size: 5,
            };
        case OperatorTypeEnum.SEARCH_DATA_SOURCE_FAQ:
            return {
                _id: operatorId,
                type: OperatorTypeEnum.SEARCH_DATA_SOURCE_FAQ,
                name: name,
                threshold: 80,
                size: 5,
            };
        case OperatorTypeEnum.LLM:
            return {
                _id: operatorId,
                type: OperatorTypeEnum.LLM,
                name: name,
                model: {...DEFAULT_LLM_MODEL},
                stream: false,
                prompts: [...DEFAULT_LLM_PROMPTS],
                truncatedOnTokenLimit: [],
                functionFormat: [],
            };
        case OperatorTypeEnum.LANGCHAIN:
            return {
                _id: operatorId,
                type: OperatorTypeEnum.LANGCHAIN,
                name: name,
                model: {...DEFAULT_LLM_MODEL},
                stream: false,
                prompts: [...DEFAULT_LLM_PROMPTS],
                truncatedOnTokenLimit: [],
                tools: null,
                toolChoice: null,
            };
        case OperatorTypeEnum.PYTHON:
            return {
                _id: operatorId,
                type: OperatorTypeEnum.PYTHON,
                name: name,
                code: DEFAULT_PYTHON_CODE,
            };
        case OperatorTypeEnum.SEARCH_DATA_SOURCE_PAGE:
            return {
                _id: operatorId,
                type: OperatorTypeEnum.SEARCH_DATA_SOURCE_PAGE,
                name: name,
            } as unknown as Operator;
        case OperatorTypeEnum.MAP_REFERENCE_DATA_SOURCE:
            return {
                _id: operatorId,
                type: OperatorTypeEnum.MAP_REFERENCE_DATA_SOURCE,
                name: name,
            } as unknown as Operator;
    }
}

export const generateDefaultNode = (
    id: string,
    operatorType: OperatorTypeEnum,
    position: {x: number; y: number}
): OperatorNode => {
    const baseData: OperatorNodeData = {
        name: id,
        type: operatorType,
        editable: true,
        borderWidth: 2
    };

    const nodeType = getOperatorNodeType(operatorType);
    switch (nodeType) {
        case "generalNode":
            return {
                id: id,
                type: nodeType,
                position: position,
                data: baseData
            };
        case "branchNode":
            return {
                id: id,
                type: nodeType,
                position: position,
                data: {...baseData, branchMap: {}}
            };
        case "llmNode":
            return {
                id: id,
                type: nodeType,
                position: position,
                data: {...baseData, model: {...DEFAULT_LLM_MODEL}}
            };
    }
}